import type { FormEvent } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Bell, Bookmark, ChevronDown, User } from 'lucide-react'
import { FiMenu, FiX } from 'react-icons/fi'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import PrimaryButton from '@presentation/components/ui/PrimaryButton'
import noSilverBullet4pdExternalLogo from '@presentation/assets/icons/no-silver-bullet-4pd-external-logo.svg'
import pdBuddyAppExternalLogo from '@presentation/assets/icons/pd-buddy-app-external-logo.svg'
import parkinsonsInsightsLogo from '@presentation/assets/icons/parkinsons-insights-logo-icon.svg'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { useUnreadNotificationsCount } from '@presentation/hooks/useUnreadNotificationsCount'

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/news', label: 'News' },
  { to: '/webinars', label: 'Webinars' },
  { to: '/dr-michael-okun-my-take', label: 'Prof Michael Okun' },
  { to: '/prof-bas-bloem-weekly', label: 'Prof Bas Bloem' },
  { to: '/hacks', label: "Michel's PD Hacks" },
]
const BRAND_MOTTO =
  "Stop being a passenger, drive your own care with a holistic approach to Parkinson's."

const DESKTOP_NAV_LINK_CLASS =
  'rounded-lg px-3 py-3 text-center text-base font-semibold whitespace-nowrap no-underline transition-colors sm:text-lg'

interface AuthenticatedUserIconsProps {
  avatarUrl?: string | null
  onProfileClick?: () => void
  onNotificationsClick?: () => void
  onFavouritesClick?: () => void
  unreadNotificationsCount?: number
  className?: string
  iconWrapperClassName?: string
  iconClassName?: string
}

function AuthenticatedUserIcons({
  avatarUrl,
  onProfileClick,
  onNotificationsClick,
  onFavouritesClick,
  unreadNotificationsCount = 0,
  className = '',
  iconWrapperClassName = 'h-11 w-11',
  iconClassName = 'h-[22px] w-[22px]',
}: AuthenticatedUserIconsProps) {
  const iconWrapperBaseClassName =
    'inline-flex items-center justify-center rounded-full border border-blue bg-blue text-white'

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {onProfileClick ? (
        <button
          type="button"
          onClick={onProfileClick}
          aria-label="Open user profile"
          className={`${iconWrapperBaseClassName} ${iconWrapperClassName} overflow-hidden transition-opacity hover:opacity-90`}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="User avatar"
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            <User className={iconClassName} aria-hidden />
          )}
        </button>
      ) : (
        <span className={`${iconWrapperBaseClassName} ${iconWrapperClassName} overflow-hidden`}>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="User avatar"
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            <User className={iconClassName} aria-hidden />
          )}
        </span>
      )}
      {onNotificationsClick ? (
        <button
          type="button"
          onClick={onNotificationsClick}
          aria-label="Open notifications"
          className={`${iconWrapperBaseClassName} ${iconWrapperClassName} relative overflow-visible transition-opacity hover:opacity-90`}
        >
          <Bell className={iconClassName} aria-hidden />
          {unreadNotificationsCount > 0 ? (
            <span className="absolute -right-2 -top-2 z-10 inline-flex h-[27px] min-w-[27px] items-center justify-center rounded-full bg-red-500 px-[6px] text-[15px] font-bold leading-none text-white shadow ring-2 ring-background">
              {unreadNotificationsCount > 9 ? '9+' : unreadNotificationsCount}
            </span>
          ) : null}
        </button>
      ) : (
        <span className={`${iconWrapperBaseClassName} ${iconWrapperClassName} relative overflow-visible`}>
          <Bell className={iconClassName} aria-hidden />
          {unreadNotificationsCount > 0 ? (
            <span className="absolute -right-2 -top-2 z-10 inline-flex h-[27px] min-w-[27px] items-center justify-center rounded-full bg-red-500 px-[6px] text-[15px] font-bold leading-none text-white shadow ring-2 ring-background">
              {unreadNotificationsCount > 9 ? '9+' : unreadNotificationsCount}
            </span>
          ) : null}
        </span>
      )}
      {onFavouritesClick ? (
        <button
          type="button"
          onClick={onFavouritesClick}
          aria-label="Open favourite news"
          className={`${iconWrapperBaseClassName} ${iconWrapperClassName} transition-opacity hover:opacity-90`}
        >
          <Bookmark className={iconClassName} aria-hidden />
        </button>
      ) : (
        <span className={`${iconWrapperBaseClassName} ${iconWrapperClassName}`}>
          <Bookmark className={iconClassName} aria-hidden />
        </span>
      )}
    </div>
  )
}

export default function Header() {
  const location = useLocation()
  const navigate = useNavigate()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [visibleDesktopNavCount, setVisibleDesktopNavCount] = useState(NAV_LINKS.length)
  const [isDesktopMoreMenuOpen, setIsDesktopMoreMenuOpen] = useState(false)
  const { user, isAuthenticated } = useAuthUser()
  const { count: unreadNotificationsCount } = useUnreadNotificationsCount(user?.uid)
  const desktopLeftGroupRef = useRef<HTMLDivElement | null>(null)
  const desktopDonateWrapperRef = useRef<HTMLDivElement | null>(null)
  const desktopNavMeasureRefs = useRef<Array<HTMLSpanElement | null>>([])
  const desktopNavMoreMeasureRef = useRef<HTMLSpanElement | null>(null)
  const desktopNavMoreWrapperRef = useRef<HTMLDivElement | null>(null)
  const currentSearchQuery = location.pathname.startsWith('/news')
    ? new URLSearchParams(location.search).get('q') ?? ''
    : ''
  const screenPath = `${location.pathname}${location.search || ''}`
  const [headerSearchQuery, setHeaderSearchQuery] = useState(currentSearchQuery)

  useEffect(() => {
    setHeaderSearchQuery(currentSearchQuery)
  }, [currentSearchQuery])

  useEffect(() => {
    setIsDesktopMoreMenuOpen(false)
  }, [location.pathname, location.search])

  useEffect(() => {
    const calculateVisibleDesktopNavCount = () => {
      const leftGroup = desktopLeftGroupRef.current

      if (!leftGroup) {
        return
      }

      const groupStyles = window.getComputedStyle(leftGroup)
      const leftGroupGap = Number.parseFloat(groupStyles.columnGap || groupStyles.gap || '0') || 0
      const donateWidth = desktopDonateWrapperRef.current?.offsetWidth ?? 0
      const availableWidth = Math.max(0, leftGroup.clientWidth - donateWidth - leftGroupGap)
      const itemGap = 8
      const itemWidths = NAV_LINKS.map(
        (_, index) => desktopNavMeasureRefs.current[index]?.offsetWidth ?? 0,
      )
      const hasAllMeasurements = itemWidths.every((width) => width > 0)

      if (!hasAllMeasurements) {
        setVisibleDesktopNavCount(NAV_LINKS.length)
        return
      }

      const moreWidth = desktopNavMoreMeasureRef.current?.offsetWidth ?? 0
      let usedWidth = 0
      let nextVisibleCount = 0

      for (let index = 0; index < itemWidths.length; index += 1) {
        const nextItemWidth = itemWidths[index]
        const nextUsedWidth = usedWidth + (nextVisibleCount > 0 ? itemGap : 0) + nextItemWidth
        const hasRemainingItems = index < itemWidths.length - 1
        const reservedMoreWidth = hasRemainingItems
          ? (nextVisibleCount > 0 ? itemGap : 0) + moreWidth
          : 0

        if (nextUsedWidth + reservedMoreWidth <= availableWidth) {
          usedWidth = nextUsedWidth
          nextVisibleCount += 1
          continue
        }

        break
      }

      setVisibleDesktopNavCount(Math.max(1, nextVisibleCount))
    }

    calculateVisibleDesktopNavCount()
    const resizeObserver = new ResizeObserver(calculateVisibleDesktopNavCount)

    if (desktopLeftGroupRef.current) {
      resizeObserver.observe(desktopLeftGroupRef.current)
    }
    if (desktopDonateWrapperRef.current) {
      resizeObserver.observe(desktopDonateWrapperRef.current)
    }

    window.addEventListener('resize', calculateVisibleDesktopNavCount)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', calculateVisibleDesktopNavCount)
    }
  }, [])

  useEffect(() => {
    if (!isDesktopMoreMenuOpen) {
      return
    }

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!desktopNavMoreWrapperRef.current?.contains(event.target as Node)) {
        setIsDesktopMoreMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', closeOnOutsideClick)

    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick)
    }
  }, [isDesktopMoreMenuOpen])

  const openAuthModal = (mode: 'login' | 'signup') => {
    setIsMobileMenuOpen(false)
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('userProfile')
    nextParams.set('authMode', mode)
    navigate({
      pathname: location.pathname,
      search: `?${nextParams.toString()}`,
    })
  }

  const openUserProfileModal = () => {
    setIsMobileMenuOpen(false)
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('authMode')
    nextParams.set('userProfile', '1')
    navigate({
      pathname: location.pathname,
      search: `?${nextParams.toString()}`,
    })
  }

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const query = headerSearchQuery.trim()
    setIsMobileMenuOpen(false)

    if (query.length > 0) {
      AnalyticsFeature.trackSearchRun(screenPath, 'header_search', isAuthenticated)
      navigate(`/news?q=${encodeURIComponent(query)}`)
      return
    }

    navigate('/news')
  }

  const clearHeaderSearch = () => {
    setHeaderSearchQuery('')
    AnalyticsFeature.trackFilterClear(screenPath, 'header_search', isAuthenticated)

    if (location.pathname.startsWith('/news') && currentSearchQuery.trim().length > 0) {
      navigate('/news')
    }
  }

  const openUserFavouritesPage = () => {
    setIsMobileMenuOpen(false)
    AnalyticsFeature.trackNavClick(screenPath, '/favorites', isAuthenticated)
    navigate('/favorites')
  }

  const openNotificationsPage = () => {
    setIsMobileMenuOpen(false)
    AnalyticsFeature.trackNavClick(screenPath, '/notifications', isAuthenticated)
    navigate('/notifications')
  }

  const trackNavClick = (destination: string) => {
    AnalyticsFeature.trackNavClick(screenPath, destination, isAuthenticated)
  }

  const trackExternalLinkClick = (source: string, contentId: string) => {
    AnalyticsFeature.trackExternalLinkOpen(
      screenPath,
      source,
      'external_link',
      contentId,
      isAuthenticated,
    )
  }

  const isNavLinkActive = (to: string) =>
    location.pathname === to || (to !== '/' && location.pathname.startsWith(to))

  const visibleDesktopNavLinks = useMemo(
    () => NAV_LINKS.slice(0, visibleDesktopNavCount),
    [visibleDesktopNavCount],
  )
  const hiddenDesktopNavLinks = useMemo(
    () => NAV_LINKS.slice(visibleDesktopNavCount),
    [visibleDesktopNavCount],
  )
  const hasActiveHiddenDesktopLink = hiddenDesktopNavLinks.some((link) => isNavLinkActive(link.to))

  return (
    <header className="text-text-on-dark">
      <div className="border-b border-border-light bg-background md:hidden">
        <div className="mx-auto max-w-7xl px-5 pb-4 pt-5">
          <div className="flex items-start justify-between gap-2">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Open menu"
              aria-expanded={isMobileMenuOpen}
              className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg text-primary transition-colors hover:bg-border-light"
            >
              <FiMenu className="h-6 w-6" />
              {isAuthenticated && unreadNotificationsCount > 0 ? (
                <span className="absolute -right-2 -top-2 z-10 inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-red-500 px-1 text-[12px] font-bold leading-none text-white shadow ring-2 ring-background">
                  {unreadNotificationsCount > 9 ? '9+' : unreadNotificationsCount}
                </span>
              ) : null}
            </button>

            <Link
              to="/"
              onClick={() => trackNavClick('/')}
              className="flex min-w-0 flex-1 flex-col items-center gap-1 text-center no-underline"
            >
              <img
                src={parkinsonsInsightsLogo}
                alt="Parkinson's Insights"
                className="h-[84px] w-auto"
              />
              <p
                className="max-w-[240px] text-xs leading-tight text-text-muted italic font-semibold"
              >
                {BRAND_MOTTO}
              </p>
            </Link>

            <div className="h-10 w-10 shrink-0" aria-hidden />
          </div>

          {!isAuthenticated || !user ? (
            <div className="mx-auto mt-4 flex w-full max-w-sm items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => openAuthModal('login')}
                className="rounded-lg px-6 py-3 text-lg font-semibold text-primary transition-colors hover:bg-border-light"
              >
                Login
              </button>
              <button
                type="button"
                onClick={() => openAuthModal('signup')}
                className="rounded-lg bg-[linear-gradient(90deg,#00A896_0%,#0056B3_50%,#00A896_100%)] bg-size-[200%_100%] bg-position-[0%_50%] px-6 py-3 text-lg font-semibold text-white shadow-sm transition-[background-position] duration-500 hover:bg-position-[100%_50%]"
              >
                Sign up
              </button>
            </div>
          ) : (
            <div className="mt-4 flex justify-center">
              <AuthenticatedUserIcons
                avatarUrl={user.avatarUrl}
                onProfileClick={openUserProfileModal}
                onNotificationsClick={openNotificationsPage}
                onFavouritesClick={openUserFavouritesPage}
                unreadNotificationsCount={unreadNotificationsCount}
                className="justify-center"
                iconWrapperClassName="h-[52px] w-[52px]"
                iconClassName="h-[26px] w-[26px]"
              />
            </div>
          )}
        </div>
      </div>
      <div className="hidden md:block">
        <div className="bg-linear-to-r from-green to-blue">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4 py-5">
              <div
                ref={desktopLeftGroupRef}
                className="flex min-w-0 flex-1 items-center gap-4"
              >
                <div className="relative min-w-0">
                  <div className="pointer-events-none absolute left-[-10000px] top-0 inline-flex items-center gap-2 opacity-0">
                    {NAV_LINKS.map((link, index) => (
                      <span
                        key={`desktop-nav-measure-${link.to}`}
                        ref={(element) => {
                          desktopNavMeasureRefs.current[index] = element
                        }}
                        className={DESKTOP_NAV_LINK_CLASS}
                      >
                        {link.label}
                      </span>
                    ))}
                    <span
                      ref={desktopNavMoreMeasureRef}
                      className={DESKTOP_NAV_LINK_CLASS}
                    >
                      More
                    </span>
                  </div>

                  <nav
                    className="flex min-w-0 items-center gap-2"
                  >
                    {visibleDesktopNavLinks.map((link) => {
                      const isActive = isNavLinkActive(link.to)
                      return (
                        <Link
                          key={link.to}
                          to={link.to}
                          onClick={() => trackNavClick(link.to)}
                          className={`${DESKTOP_NAV_LINK_CLASS} ${
                            isActive
                              ? 'bg-white/20 text-white'
                              : 'text-white hover:bg-white/10'
                          }`}
                        >
                          {link.label}
                        </Link>
                      )
                    })}

                    {hiddenDesktopNavLinks.length > 0 && (
                      <div
                        ref={desktopNavMoreWrapperRef}
                        className="relative shrink-0"
                      >
                        <button
                          type="button"
                          onClick={() => setIsDesktopMoreMenuOpen((current) => !current)}
                          aria-label="Open more navigation links"
                          aria-expanded={isDesktopMoreMenuOpen}
                          className={`${DESKTOP_NAV_LINK_CLASS} inline-flex items-center gap-1 ${
                            hasActiveHiddenDesktopLink
                              ? 'bg-white/20 text-white'
                              : 'text-white hover:bg-white/10'
                          }`}
                        >
                          More
                          <ChevronDown className="h-4 w-4" aria-hidden />
                        </button>

                        {isDesktopMoreMenuOpen && (
                          <div className="absolute left-0 top-full z-30 mt-2 min-w-[240px] rounded-xl border border-border-light bg-surface p-1 shadow-lg">
                            {hiddenDesktopNavLinks.map((link) => {
                              const isActive = isNavLinkActive(link.to)
                              return (
                                <Link
                                  key={`desktop-hidden-${link.to}`}
                                  to={link.to}
                                  onClick={() => {
                                    trackNavClick(link.to)
                                    setIsDesktopMoreMenuOpen(false)
                                  }}
                                  className={`block rounded-lg px-3 py-2.5 text-sm font-semibold no-underline transition-colors ${
                                    isActive
                                      ? 'bg-primary text-white'
                                      : 'text-text-primary hover:bg-border-light'
                                  }`}
                                >
                                  {link.label}
                                </Link>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </nav>
                </div>

                <div ref={desktopDonateWrapperRef} className="shrink-0">
                  <PrimaryButton
                    href="https://buy.stripe.com/8x2eVe5odaaTc6ibRH8EM05"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackExternalLinkClick(
                        'header_donate_desktop',
                        'https://buy.stripe.com/8x2eVe5odaaTc6ibRH8EM05',
                      )
                    }}
                    className="px-7 py-3 text-lg"
                  >
                    Donate
                  </PrimaryButton>
                </div>
              </div>

              <div className="flex w-[255px] shrink-0 flex-col gap-2">
                <a
                  href="https://pdbuddy.app/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackExternalLinkClick('header_desktop', 'https://pdbuddy.app/')}
                  aria-label="Open PD Buddy App"
                  className="inline-flex h-[52px] w-full items-center justify-center rounded-lg border border-white/80 px-4 py-2 text-center text-base font-semibold text-white no-underline transition-colors hover:border-white"
                >
                  <img
                    src={pdBuddyAppExternalLogo}
                    alt="PD Buddy App"
                    className="h-full w-auto max-w-full object-contain"
                  />
                </a>
                <a
                  href="https://nosilverbullet4pd.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackExternalLinkClick('header_desktop', 'https://nosilverbullet4pd.com/')}
                  aria-label="Open No Silver Bullet 4PD"
                  className="inline-flex h-12 w-full items-center justify-center rounded-lg border border-white/80 px-4 py-2 text-center text-base font-semibold text-white no-underline transition-colors hover:border-white"
                >
                  <img
                    src={noSilverBullet4pdExternalLogo}
                    alt="No Silver Bullet 4PD"
                    className="h-full w-auto max-w-full object-contain"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-b border-border-light bg-background">
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 items-center justify-items-center gap-4 md:grid-cols-3">
              <form
                onSubmit={handleSearchSubmit}
                className="relative mx-auto w-full max-w-md md:mx-0 md:justify-self-start"
              >
                <svg
                  className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  name="headerSearch"
                  value={headerSearchQuery}
                  onChange={(event) => setHeaderSearchQuery(event.target.value)}
                  placeholder="Search news by keywords..."
                  className="w-full rounded-xl border border-slate-200 bg-background py-3 pl-10 pr-11 text-base font-semibold text-black transition-colors placeholder:text-slate-400 focus:border-blue! focus:outline-none! focus:ring-0! focus-visible:rounded-xl! focus-visible:border-blue! focus-visible:outline-none! focus-visible:outline-offset-0! focus-visible:ring-0!"
                />
                {headerSearchQuery.trim().length > 0 && (
                  <button
                    type="button"
                    onClick={clearHeaderSearch}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-slate-400 transition-colors hover:text-primary"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                )}
              </form>

              <Link
                to="/"
                onClick={() => trackNavClick('/')}
                className="mx-auto flex flex-col items-center gap-1 pb-1 pt-2 text-center no-underline"
              >
                <img
                  src={parkinsonsInsightsLogo}
                  alt="Parkinson's Insights"
                  className="h-32 w-auto sm:h-36"
                />
                <p
                  className="max-w-[340px] text-xs leading-tight text-text-muted italic font-semibold sm:text-sm"
                >
                  {BRAND_MOTTO}
                </p>
              </Link>

              <div className="flex items-center justify-center gap-2 md:justify-self-end">
                {isAuthenticated && user ? (
                  <AuthenticatedUserIcons
                    avatarUrl={user.avatarUrl}
                    onProfileClick={openUserProfileModal}
                    onNotificationsClick={openNotificationsPage}
                    onFavouritesClick={openUserFavouritesPage}
                    unreadNotificationsCount={unreadNotificationsCount}
                    iconWrapperClassName="h-12 w-12 lg:h-14 lg:w-14"
                    iconClassName="h-6 w-6 lg:h-7 lg:w-7"
                  />
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => openAuthModal('login')}
                      className="rounded-lg px-6 py-3 text-lg font-semibold text-primary transition-colors hover:bg-border-light"
                    >
                      Login
                    </button>
                    <button
                      type="button"
                      onClick={() => openAuthModal('signup')}
                      className="rounded-lg bg-[linear-gradient(90deg,#00A896_0%,#0056B3_50%,#00A896_100%)] bg-size-[200%_100%] bg-position-[0%_50%] px-6 py-3 text-lg font-semibold text-white shadow-sm transition-[background-position] duration-500 hover:bg-position-[100%_50%]"
                    >
                      Sign up
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`fixed inset-0 z-50 md:hidden ${
          isMobileMenuOpen ? '' : 'pointer-events-none'
        }`}
      >
        <button
          type="button"
          aria-label="Close menu backdrop"
          onClick={() => setIsMobileMenuOpen(false)}
          className={`absolute inset-0 bg-black/45 transition-opacity duration-300 ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
        />

        <aside
          className={`absolute inset-y-0 left-0 flex w-full flex-col bg-linear-to-b from-green to-blue text-white shadow-xl transition-transform duration-300 ${
            isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <div className="border-b border-white/20">
            <div className="flex items-center justify-between px-4 py-3">
              <p className="text-base font-semibold text-white">Menu</p>
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setIsMobileMenuOpen(false)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-white transition-colors hover:bg-white/15"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-5">
            <div className="space-y-5">
              <section>
                <form
                  onSubmit={handleSearchSubmit}
                  className="relative"
                >
                  <svg
                    className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-text-muted"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    name="headerSearch"
                  value={headerSearchQuery}
                  onChange={(event) => setHeaderSearchQuery(event.target.value)}
                    placeholder="Search news by keywords..."
                  className="w-full rounded-xl border border-border bg-background py-3.5 pl-11 pr-11 text-base font-semibold text-black shadow-sm transition-colors placeholder:text-text-muted focus:border-blue! focus:outline-none! focus:ring-0! focus-visible:rounded-xl! focus-visible:border-blue! focus-visible:outline-none! focus-visible:outline-offset-0! focus-visible:ring-0!"
                  />
                {headerSearchQuery.trim().length > 0 && (
                  <button
                    type="button"
                    onClick={clearHeaderSearch}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-text-muted transition-colors hover:text-primary"
                  >
                    <FiX className="h-4 w-4" />
                  </button>
                )}
                </form>
              </section>

              <section>
                <nav className="space-y-2">
                  {NAV_LINKS.map((link) => {
                    const isActive = location.pathname === link.to ||
                      (link.to !== '/' && location.pathname.startsWith(link.to))
                    return (
                      <Link
                        key={`mobile-${link.to}`}
                        to={link.to}
                        onClick={() => {
                          trackNavClick(link.to)
                          setIsMobileMenuOpen(false)
                        }}
                        className={`block w-full rounded-lg px-4 py-3 text-center text-base font-semibold no-underline transition-colors ${
                          isActive
                            ? 'border border-white/55 bg-white/40 text-white'
                            : 'border border-white/40 bg-transparent text-white hover:bg-white/10'
                        }`}
                      >
                        {link.label}
                      </Link>
                    )
                  })}
                </nav>
              </section>

              <section>
                <PrimaryButton
                  href="https://buy.stripe.com/8x2eVe5odaaTc6ibRH8EM05"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-white! py-3 text-primary! hover:bg-white! hover:opacity-90"
                  onClick={() => {
                    trackExternalLinkClick(
                      'header_donate_mobile',
                      'https://buy.stripe.com/8x2eVe5odaaTc6ibRH8EM05',
                    )
                    setIsMobileMenuOpen(false)
                  }}
                >
                  Donate
                </PrimaryButton>
              </section>

              <section>
                <div className="space-y-2">
                  <a
                    href="https://pdbuddy.app/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackExternalLinkClick('header_mobile', 'https://pdbuddy.app/')
                      setIsMobileMenuOpen(false)
                    }}
                    aria-label="Open PD Buddy App"
                    className="inline-flex h-[52px] w-full items-center justify-center rounded-lg border border-white/40 bg-blue px-3 py-2 text-base font-semibold text-white no-underline transition-colors hover:border-white/55 hover:bg-blue/90"
                  >
                    <img
                      src={pdBuddyAppExternalLogo}
                      alt="PD Buddy App"
                      className="h-full w-auto max-w-full object-contain"
                    />
                  </a>
                  <a
                    href="https://nosilverbullet4pd.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackExternalLinkClick('header_mobile', 'https://nosilverbullet4pd.com/')
                      setIsMobileMenuOpen(false)
                    }}
                    aria-label="Open No Silver Bullet 4PD"
                    className="inline-flex h-12 w-full items-center justify-center rounded-lg border border-white/40 bg-blue px-3 py-2 text-base font-semibold text-white no-underline transition-colors hover:border-white/55 hover:bg-blue/90"
                  >
                    <img
                      src={noSilverBullet4pdExternalLogo}
                      alt="No Silver Bullet 4PD"
                      className="h-full w-auto max-w-full object-contain"
                    />
                  </a>
                </div>
              </section>

            </div>
          </div>
        </aside>
      </div>
    </header>
  )
}
